import * as THREE from 'three';
import { Random } from '../core/Random';
import { couch } from './builders/props';
import { bed, dreamCatcher, dresser } from './builders/furniture';
import type { CollisionWorld } from './Collision';
import type { Vec2 } from './layout';
import { box, cyl, M, mat, mesh, sphere, texMat } from './materials';
import { PublicRoom } from './PublicRoom';
import { BRENDA_SLOT, HANK_SLOT, WANDA_SLOT } from './rooms';
import {
  foilTexture,
  panelingTexture,
  shagTexture,
  signTexture,
  woodTexture,
  type SignSpec,
} from './textures';

/**
 * Inside the three trailers at the north end of Main Street, the ones that never had
 * anybody home: Brenda's Beauty Barn, Madame Wanda's and Hank's bunker. Single-wide
 * rooms (11.6 x 3.6, door at local x = 4 on the +z wall).
 */

export const BRENDA_SPOTS = {
  chair: { x: 1.6, z: -0.55 },
  brenda: { x: 2.55, z: -0.35 },
  dryer: { x: -0.7, z: -1.2 },
} as const satisfies Record<string, Vec2>;

export const WANDA_SPOTS = {
  table: { x: 0.6, z: -0.3 },
  wanda: { x: 0.6, z: -1.25 },
  seat: { x: 0.6, z: 0.75 },
  cat: { x: -3.6, z: 1.2 },
} as const satisfies Record<string, Vec2>;

export const HANK_SPOTS = {
  radio: { x: 3.0, z: -1.45 },
  hank: { x: 2.2, z: -0.5 },
  board: { x: -1.0, z: -1.75 },
  shelves: { x: -3.6, z: -1.45 },
} as const satisfies Record<string, Vec2>;

export function poster(spec: SignSpec, w: number, h: number, glow = 0): THREE.Mesh {
  const tex = signTexture(spec);
  return mesh(
    new THREE.PlaneGeometry(w, h),
    texMat(tex, glow ? { emissive: '#ffffff', emissiveIntensity: glow } : {}),
    0,
    0,
    0,
    { cast: false },
  );
}

/** A partition wall `length` long along local z (place it at its center). */
export function partition(length: number, material: THREE.Material): THREE.Mesh {
  return box(0.1, 2.0, length, material, 0, 1.0, 0);
}

// --- Brenda's Beauty Barn ---------------------------------------------------------------------

export interface Salon {
  room: PublicRoom;
  chair: THREE.Object3D;
}

export function buildBrendaInterior(parent: THREE.Object3D, collision: CollisionWorld): Salon {
  let chair: THREE.Object3D = new THREE.Group();
  const room = new PublicRoom(
    parent,
    collision,
    BRENDA_SLOT,
    'single',
    {
      wall: mat('#f3cfe0', { rough: 0.9 }),
      floor: texMat(shagTexture('#e8a8c8'), { rough: 1 }),
      ceiling: mat('#f4ecf0', { rough: 1 }),
    },
    (kit) => {
      const { depth: D } = kit.shape;
      // The barber chair: chrome post, red vinyl, a footrest.
      const c = new THREE.Group();
      c.add(cyl(0.28, 0.32, 0.08, M.chrome, 0, 0.04, 0, 14));
      c.add(cyl(0.06, 0.06, 0.4, M.chrome, 0, 0.26, 0, 8));
      c.add(box(0.6, 0.12, 0.55, mat('#b5262e', { rough: 0.4 }), 0, 0.5, 0));
      c.add(box(0.6, 0.6, 0.1, mat('#b5262e', { rough: 0.4 }), 0, 0.85, -0.25));
      for (const s of [-1, 1]) c.add(box(0.08, 0.08, 0.5, M.chrome, s * 0.33, 0.68, 0));
      c.add(box(0.45, 0.04, 0.2, M.chrome, 0, 0.18, 0.38));
      chair = kit.place(c, BRENDA_SPOTS.chair.x, BRENDA_SPOTS.chair.z, Math.PI);
      kit.circle(BRENDA_SPOTS.chair.x, BRENDA_SPOTS.chair.z, 0.35);
      // A big mirror and a counter of products on the back wall.
      kit.add(box(1.6, 0.06, 0.45, mat('#f4f4f0'), 0, 0.9, 0), BRENDA_SPOTS.chair.x, -D / 2 + 0.25);
      kit.add(box(1.6, 0.86, 0.42, mat('#d98cb3'), 0, 0.43, 0), BRENDA_SPOTS.chair.x, -D / 2 + 0.24);
      kit.solid(BRENDA_SPOTS.chair.x, -D / 2 + 0.24, 1.6, 0.45);
      kit.add(
        box(
          1.5,
          1.0,
          0.03,
          mat('#d8e8f0', { rough: 0.1, metal: 0.2, emissive: '#8aa6b8', emissiveIntensity: 0.35 }),
          0,
          1.55,
          0,
        ),
        BRENDA_SPOTS.chair.x,
        -D / 2 + 0.03,
      );
      const rng = new Random(77);
      for (let i = 0; i < 8; i++)
        kit.add(
          cyl(
            0.035,
            0.04,
            0.2,
            mat(rng.pick(['#ff6fa8', '#ffd400', '#9b5de5', '#2a9d8f', '#f4f4f0']), { rough: 0.3 }),
            0,
            1.03,
            0,
            8,
          ),
          BRENDA_SPOTS.chair.x - 0.7 + i * 0.2,
          -D / 2 + 0.2,
        );
      // The hood dryer chair, straight out of 1986.
      const d = new THREE.Group();
      d.add(box(0.55, 0.45, 0.5, mat('#ff9cc6', { rough: 0.5 }), 0, 0.22, 0));
      d.add(box(0.55, 0.55, 0.1, mat('#ff9cc6', { rough: 0.5 }), 0, 0.7, -0.22));
      d.add(cyl(0.03, 0.03, 1.1, M.chrome, 0, 0.9, -0.3, 6));
      const hood = sphere(0.3, mat('#f4f4f0', { rough: 0.3 }), 0, 1.3, -0.12, 12);
      hood.scale.set(1, 0.8, 1);
      d.add(hood);
      kit.add(d, BRENDA_SPOTS.dryer.x, BRENDA_SPOTS.dryer.z);
      kit.solid(BRENDA_SPOTS.dryer.x, BRENDA_SPOTS.dryer.z, 0.6, 0.6);
      // Shampoo sink by the door.
      kit.add(box(0.7, 0.9, 0.55, mat('#3a3d42'), 0, 0.45, 0), 3.7, -D / 2 + 0.3);
      kit.add(box(0.5, 0.12, 0.4, mat('#f4f4f0'), 0, 0.95, 0), 3.7, -D / 2 + 0.3);
      kit.solid(3.7, -D / 2 + 0.3, 0.7, 0.55);
      // Her living room past the partition: couch, a cat bed, the good TV.
      const wall = mat('#f3cfe0', { rough: 0.9 });
      kit.add(partition(1.6, wall), -2.4, -1.0);
      kit.solid(-2.4, -1.0, 0.12, 1.6);
      kit.add(couch('#9b5de5'), -4.2, -1.25);
      kit.solid(-4.2, -1.25, 2.0, 0.9);
      kit.add(dresser(), -4.6, 1.45, Math.PI);
      kit.solid(-4.6, 1.45, 1.1, 0.5);
      const posters: Array<[string, string, number]> = [
        ['THE MULLET', '#1b1b1b', -1.2],
        ['THE RACHEL', '#ff6fa8', 0.2],
      ];
      for (const [text, color, x] of posters) {
        const p = poster(
          {
            w: 256,
            h: 320,
            bg: '#fff3f8',
            fg: color,
            border: color,
            borderWidth: 8,
            lines: [{ text, size: 44, font: 'Anton' }],
          },
          0.6,
          0.75,
        );
        p.position.set(x, 1.5, -D / 2 + 0.02);
        kit.walls.back.add(p);
      }
      const welcome = poster(
        {
          w: 512,
          h: 192,
          bg: '#ff6fa8',
          fg: '#ffffff',
          lines: [
            { text: 'WALK-INS WELCOME', size: 64, font: 'Bangers' },
            { text: 'NO REFUNDS ON BANGS', size: 34, font: 'Anton', color: '#2b0a1a' },
          ],
        },
        1.2,
        0.45,
        0.4,
      );
      welcome.position.set(2.4, 1.9, D / 2 - 0.02);
      welcome.rotation.y = Math.PI;
      kit.walls.front.add(welcome);
    },
  );
  return { room, chair };
}

// --- Madame Wanda -------------------------------------------------------------------------------

export interface Parlor {
  room: PublicRoom;
  ball: THREE.Mesh;
  cat: THREE.Object3D;
}

function cat(color: string): THREE.Group {
  const g = new THREE.Group();
  const fur = mat(color, { rough: 0.9 });
  const body = sphere(0.14, fur, 0, 0.14, 0, 8);
  body.scale.set(1.5, 0.9, 0.9);
  g.add(body);
  g.add(sphere(0.09, fur, 0.2, 0.25, 0, 8));
  for (const s of [-1, 1]) {
    const ear = cyl(0.0, 0.035, 0.07, fur, 0.21, 0.35, s * 0.045, 4);
    g.add(ear);
  }
  const tail = cyl(0.02, 0.02, 0.3, fur, -0.24, 0.25, 0, 5);
  tail.rotation.z = 0.6;
  g.add(tail);
  for (const s of [-1, 1])
    g.add(
      box(
        0.02,
        0.02,
        0.01,
        mat('#c9e84a', { emissive: '#c9e84a', emissiveIntensity: 0.8 }),
        0.285,
        0.27,
        s * 0.035,
      ),
    );
  return g;
}

export function buildWandaInterior(parent: THREE.Object3D, collision: CollisionWorld): Parlor {
  const ballMat = new THREE.MeshStandardMaterial({
    color: '#b8a4ff',
    emissive: new THREE.Color('#7a5cff'),
    emissiveIntensity: 1,
    roughness: 0.05,
    transparent: true,
    opacity: 0.85,
  });
  const ball = sphere(0.16, ballMat, 0, 0.98, 0, 18);
  let theCat: THREE.Object3D = new THREE.Group();
  const flames: THREE.MeshStandardMaterial[] = [];
  const room = new PublicRoom(
    parent,
    collision,
    WANDA_SLOT,
    'single',
    {
      wall: mat('#3a1f4a', { rough: 0.95 }),
      floor: texMat(shagTexture('#5a2a3a'), { rough: 1 }),
      ceiling: mat('#2a1636', { rough: 1 }),
    },
    (kit) => {
      const { depth: D } = kit.shape;
      const t = WANDA_SPOTS.table;
      // A round table under a velvet cloth, the crystal ball, candles and the cards.
      kit.add(cyl(0.55, 0.62, 0.78, mat('#6b1f3a', { rough: 0.8 }), 0, 0.39, 0, 18), t.x, t.z);
      kit.circle(t.x, t.z, 0.62);
      kit.place(ball, t.x, t.z);
      kit.add(cyl(0.08, 0.1, 0.06, mat('#c9a23a', { metal: 0.8 }), 0, 0.81, 0, 10), t.x, t.z);
      for (const [dx, dz] of [
        [-0.35, -0.2],
        [0.38, -0.1],
        [0.1, 0.35],
      ] as const) {
        kit.add(cyl(0.03, 0.03, 0.18, mat('#f4ecd8'), 0, 0.87, 0, 8), t.x + dx, t.z + dz);
        const f = new THREE.MeshStandardMaterial({
          color: '#ffd27a',
          emissive: new THREE.Color('#ff9a2a'),
          emissiveIntensity: 2,
        });
        flames.push(f);
        kit.place(
          mesh(new THREE.ConeGeometry(0.02, 0.06, 6), f, 0, 0.99, 0, { cast: false }),
          t.x + dx,
          t.z + dz,
        );
      }
      const card = mat('#f4ecd8');
      for (let i = 0; i < 3; i++)
        kit.add(box(0.09, 0.005, 0.14, card, 0, 0.785, 0), t.x - 0.15 + i * 0.15, t.z + 0.22, 0.1 - i * 0.1);
      // Her chair, and yours.
      kit.add(box(0.5, 0.06, 0.5, mat('#3a2414'), 0, 0.45, 0), WANDA_SPOTS.seat.x, WANDA_SPOTS.seat.z + 0.1);
      kit.add(
        box(0.5, 0.06, 0.5, mat('#3a2414'), 0, 0.45, 0),
        WANDA_SPOTS.wanda.x,
        WANDA_SPOTS.wanda.z - 0.1,
      );
      // A beaded curtain to the back room.
      const beadCols = ['#c62828', '#ffd400', '#9b5de5', '#2a9d8f'];
      for (let i = 0; i < 18; i++)
        kit.add(
          cyl(0.006, 0.006, 1.9, mat(beadCols[i % 4] as string, { rough: 0.3 }), 0, 1.25, 0, 4),
          -2.2,
          -1.7 + i * 0.2,
        );
      kit.add(bed('#3a1f4a', 'twin'), -4.6, -0.6, Math.PI / 2);
      kit.solid(-4.6, -0.6, 2.1, 1.0);
      // Shelves of jars and a skull that's "just a decoration."
      const shelf = texMat(woodTexture(), { color: '#3a2414' });
      for (const y of [1.2, 1.6]) {
        kit.add(box(1.4, 0.04, 0.3, shelf, 0, y, 0), 3.2, -D / 2 + 0.17);
        for (let i = 0; i < 5; i++)
          kit.add(
            cyl(
              0.06,
              0.06,
              0.16,
              mat(['#7a9a3a', '#c62828', '#9b5de5', '#d9b45a', '#2a9d8f'][i] as string, {
                transparent: true,
                opacity: 0.8,
              }),
              0,
              y + 0.1,
              0,
              8,
            ),
            2.65 + i * 0.27,
            -D / 2 + 0.17,
          );
      }
      kit.add(sphere(0.09, mat('#f4ecd8'), 0, 1.72, 0, 8), 3.85, -D / 2 + 0.17);
      const dc = dreamCatcher();
      dc.position.set(-1.2, 1.7, -D / 2 + 0.03);
      kit.walls.back.add(dc);
      theCat = kit.place(cat('#2b2b2b'), WANDA_SPOTS.cat.x, WANDA_SPOTS.cat.z, -0.6);
      kit.place(cat('#e8a050'), -1.4, 1.25, 2.4);
      const sign = poster(
        {
          w: 512,
          h: 192,
          bg: '#1a0d22',
          fg: '#ffd400',
          border: '#ffd400',
          borderWidth: 6,
          lines: [
            { text: 'THE SPIRITS', size: 54, font: 'Rye' },
            { text: 'ACCEPT CASH', size: 54, font: 'Rye', color: '#ff9ad5' },
          ],
        },
        1.1,
        0.42,
        0.5,
      );
      sign.position.set(0.6, 1.75, -D / 2 + 0.02);
      kit.walls.back.add(sign);
      kit.animate((time) => {
        ballMat.emissiveIntensity = 0.8 + Math.sin(time * 1.3) * 0.35 + Math.sin(time * 3.7) * 0.1;
        ball.rotation.y = time * 0.3;
        flames.forEach((f, i) => (f.emissiveIntensity = 1.8 + Math.sin(time * 13 + i * 2) * 0.4));
      });
    },
  );
  return { room, ball, cat: theCat };
}

// --- Hank's bunker -------------------------------------------------------------------------------

export interface Bunker {
  room: PublicRoom;
  radio: THREE.Object3D;
  board: THREE.Object3D;
}

export function buildHankInterior(parent: THREE.Object3D, collision: CollisionWorld): Bunker {
  let radio: THREE.Object3D = new THREE.Group();
  let board: THREE.Object3D = new THREE.Group();
  const leds: THREE.MeshStandardMaterial[] = [];
  const room = new PublicRoom(
    parent,
    collision,
    HANK_SLOT,
    'single',
    {
      wall: texMat(foilTexture(), { metal: 0.7, rough: 0.35 }),
      floor: texMat(panelingTexture(), { color: '#6b6a5a' }),
      ceiling: texMat(foilTexture(), { metal: 0.7, rough: 0.35 }),
    },
    (kit) => {
      const { depth: D } = kit.shape;
      const rng = new Random(15);
      // Prepper shelves: cans, jugs of water, a bucket labeled "BEANS (DO NOT EAT)".
      const s = HANK_SPOTS.shelves;
      const metal = mat('#7a8270', { metal: 0.4 });
      for (const y of [0.1, 0.6, 1.1, 1.6]) {
        kit.add(box(2.2, 0.04, 0.45, metal, 0, y, 0), s.x, s.z);
        for (let i = 0; i < 9; i++)
          kit.add(
            cyl(
              0.05,
              0.05,
              0.13,
              mat(rng.pick(['#c62828', '#c9ced3', '#2f7d32', '#d9b45a']), { metal: 0.5 }),
              0,
              y + 0.09,
              0,
              8,
            ),
            s.x - 1 + i * 0.24,
            s.z + rng.range(-0.1, 0.1),
          );
      }
      for (const x of [-1.05, 1.05]) kit.add(box(0.05, 2, 0.05, metal, 0, 1, 0), s.x + x, s.z);
      kit.solid(s.x, s.z, 2.2, 0.5);
      for (let i = 0; i < 4; i++)
        kit.add(
          box(0.25, 0.35, 0.25, mat('#5aa9e6', { transparent: true, opacity: 0.7 }), 0, 0.18, 0),
          -5.2 + i * 0.3,
          0.6,
        );
      // The ham radio station, all knobs and blinking lights.
      const r = HANK_SPOTS.radio;
      const desk = new THREE.Group();
      desk.add(box(1.4, 0.06, 0.6, texMat(woodTexture(), { color: '#5a4630' }), 0, 0.76, 0));
      for (const x of [-0.65, 0.65])
        desk.add(box(0.06, 0.76, 0.55, texMat(woodTexture(), { color: '#5a4630' }), x, 0.38, 0));
      desk.add(box(0.6, 0.28, 0.35, mat('#2b2b2e', { metal: 0.4 }), -0.25, 0.93, -0.05));
      desk.add(box(0.4, 0.22, 0.3, mat('#3a3a2e', { metal: 0.4 }), 0.35, 0.9, -0.05));
      for (let i = 0; i < 6; i++) {
        const led = new THREE.MeshStandardMaterial({
          color: '#111',
          emissive: new THREE.Color(i % 2 ? '#ff3030' : '#39ff5a'),
          emissiveIntensity: 1,
        });
        leds.push(led);
        desk.add(box(0.025, 0.025, 0.01, led, -0.45 + i * 0.07, 1.0, 0.125, { cast: false }));
      }
      desk.add(cyl(0.012, 0.012, 0.25, M.black, 0.25, 0.92, 0.18, 4));
      desk.add(sphere(0.035, M.black, 0.25, 1.06, 0.18, 6));
      radio = kit.place(desk, r.x, r.z);
      kit.solid(r.x, r.z, 1.4, 0.6);
      // The wall: a map of the county, photos, and red string connecting everything.
      const wall = new THREE.Group();
      wall.add(box(1.8, 1.1, 0.03, mat('#b08860'), 0, 0, 0));
      const map = poster(
        {
          w: 512,
          h: 320,
          bg: '#e8dcb0',
          fg: '#3a2a1a',
          lines: [
            { text: 'LUCKY ACRES', size: 44, font: 'Rye' },
            { text: '✕ SIGHTINGS ✕', size: 40, font: 'Anton', color: '#c62828' },
          ],
        },
        0.9,
        0.56,
      );
      map.position.set(-0.35, 0.1, 0.02);
      wall.add(map);
      const pins: THREE.Vector3[] = [];
      for (let i = 0; i < 7; i++) {
        const p = new THREE.Vector3(rng.range(-0.8, 0.8), rng.range(-0.45, 0.45), 0.03);
        pins.push(p);
        wall.add(
          box(0.14, 0.1, 0.01, mat(rng.pick(['#f4f4f0', '#d9d4c7'])), p.x, p.y, 0.02, { cast: false }),
        );
      }
      const string = mat('#c62828');
      for (let i = 0; i < pins.length - 1; i++) {
        const a = pins[i] as THREE.Vector3;
        const b = pins[(i + 3) % pins.length] as THREE.Vector3;
        const len = a.distanceTo(b);
        const seg = box(len, 0.008, 0.008, string, (a.x + b.x) / 2, (a.y + b.y) / 2, 0.035, { cast: false });
        seg.rotation.z = Math.atan2(b.y - a.y, b.x - a.x);
        wall.add(seg);
      }
      wall.position.set(HANK_SPOTS.board.x, 1.5, -D / 2 + 0.03);
      kit.walls.back.add(wall);
      board = wall;
      // A gun safe, an army cot, a hazmat suit on a mannequin.
      kit.add(box(0.8, 1.6, 0.7, mat('#2f3a2a', { metal: 0.5 }), 0, 0.8, 0), -5.2, -1.3);
      kit.add(cyl(0.08, 0.08, 0.04, M.chrome, 0, 1.0, 0, 10).rotateX(Math.PI / 2), -5.2, -0.93);
      kit.solid(-5.2, -1.3, 0.8, 0.7);
      kit.add(box(1.9, 0.08, 0.75, mat('#5a6b3a'), 0, 0.42, 0), -3.6, 1.2);
      for (const x of [-0.85, 0.85]) kit.add(box(0.05, 0.4, 0.7, M.steel, 0, 0.2, 0), -3.6 + x, 1.2);
      kit.solid(-3.6, 1.2, 1.9, 0.75);
      const suit = new THREE.Group();
      const yellow = mat('#e8d32a', { rough: 0.6 });
      suit.add(box(0.45, 0.7, 0.28, yellow, 0, 1.2, 0));
      suit.add(box(0.36, 0.8, 0.24, yellow, 0, 0.45, 0));
      suit.add(sphere(0.17, yellow, 0, 1.72, 0, 10));
      suit.add(box(0.24, 0.12, 0.02, M.darkGlass, 0, 1.74, 0.16));
      kit.add(suit, 0.7, 1.35, Math.PI);
      kit.solid(0.7, 1.35, 0.5, 0.35);
      const keepOut = poster(
        {
          w: 512,
          h: 256,
          bg: '#111111',
          fg: '#ffd400',
          border: '#ffd400',
          borderWidth: 10,
          lines: [
            { text: 'THEY ARE', size: 60, font: 'Anton' },
            { text: 'WATCHING', size: 80, font: 'Anton', color: '#ff4b4b' },
          ],
        },
        0.8,
        0.4,
      );
      keepOut.position.set(3.0, 1.75, -D / 2 + 0.02);
      kit.walls.back.add(keepOut);
      kit.animate((time) =>
        leds.forEach((l, i) => (l.emissiveIntensity = Math.sin(time * (3 + i) + i) > 0 ? 1.4 : 0.1)),
      );
    },
  );
  return { room, radio, board };
}
